import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Cake, Leaf, MessageCircle, Truck } from "lucide-react";
import Sprig from "./Sprig";
import { scrollToSection } from "@/lib/scroll";
import { WHATSAPP_URL } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const LINES = [
    { text: "Handmade cakes", italic: false },
    { text: "for every", italic: false },
    { text: "celebration.", italic: true },
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const photoY = useTransform(scrollYProgress, [0, 1], [0, 70]);

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden pb-16 pt-24 sm:pt-28 lg:pb-24 lg:pt-32"
        >
            <Sprig className="absolute -left-6 top-44 hidden w-20 animate-sway text-leaf/20 sm:block lg:w-24" />
            <Sprig
                className="absolute bottom-16 right-[6%] hidden w-14 -scale-x-100 animate-sway text-leaf/15 [animation-delay:1.4s] sm:block"
                style={{ transformOrigin: "bottom center" }}
            />

            <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
                        className="chip"
                    >
                        <Leaf className="size-3.5 text-leaf" />
                        Handmade in Colombo, Sri Lanka
                    </motion.p>

                    <h1 className="mt-6 font-serif text-[2.85rem] font-semibold leading-[1.04] tracking-tight text-cocoa sm:text-6xl lg:text-7xl">
                        {LINES.map((line, i) => (
                            <span key={line.text} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.italic ? "italic text-leaf" : ""}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        duration: 0.9,
                                        delay: 0.15 + i * 0.15,
                                        ease: EASE,
                                    }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
                        className="mt-6 max-w-md text-base leading-relaxed text-cocoa-soft sm:text-lg"
                    >
                        Weddings, birthdays, vintage cakes, engagements & every little
                        moment worth celebrating — iced by hand, one order at a time.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
                        className="mt-8 flex flex-wrap items-center gap-3"
                    >
                        <button
                            type="button"
                            data-testid="hero-order-cta"
                            onClick={() => scrollToSection("#order")}
                            className="btn-primary"
                        >
                            Order your cake
                            <ArrowDown className="size-4" />
                        </button>
                        <a
                            data-testid="hero-whatsapp-cta"
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-outline"
                        >
                            <MessageCircle className="size-4" />
                            WhatsApp us
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.7, delay: 1.05 }}
                        className="mt-7 flex items-center gap-2 text-sm text-cocoa-soft"
                    >
                        <Truck className="size-4 text-gold" />
                        Delivery & pickup available
                    </motion.p>
                </div>

                <div className="relative mx-auto w-full max-w-[300px] sm:max-w-[380px] lg:max-w-[420px]">
                    <motion.div style={{ y: photoY }} className="relative">
                        <div
                            aria-hidden
                            className="absolute -inset-3 rounded-t-full rounded-b-[2.5rem] border border-gold/50 sm:-inset-4"
                        />
                        <Sprig className="absolute -left-10 top-8 w-14 animate-sway text-leaf/25" />
                        <motion.img
                            src="/images/cake-wedding.jpg"
                            alt="Three-tier ivory wedding cake with sugar roses and gold initials, made by Cakes'alot in Colombo"
                            data-testid="hero-photo"
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, delay: 0.35, ease: EASE }}
                            className="aspect-[3/4] w-full rounded-t-full rounded-b-[2.5rem] object-cover shadow-[0_40px_80px_-40px_rgba(44,34,30,0.4)]"
                        />

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1, ease: EASE }}
                            className="absolute -right-2 top-12 animate-floaty rounded-full bg-white/95 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-leaf shadow-lg ring-1 ring-blush/50 sm:-right-6 sm:text-xs"
                        >
                            Delivery & pickup
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.7, delay: 1.15, ease: EASE }}
                            className="absolute -bottom-8 -left-6 grid size-24 place-items-center rounded-full bg-cream shadow-lg ring-1 ring-blush/60 sm:-left-10 sm:size-28"
                        >
                            <svg
                                viewBox="0 0 100 100"
                                className="absolute inset-0 size-full animate-spin-slow"
                                aria-hidden
                            >
                                <defs>
                                    <path
                                        id="hero-badge-circle"
                                        d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0"
                                        fill="none"
                                    />
                                </defs>
                                <text
                                    className="fill-cocoa font-semibold uppercase"
                                    style={{ fontSize: "7.2px", letterSpacing: "0.6px" }}
                                >
                                    <textPath
                                        href="#hero-badge-circle"
                                        textLength="226"
                                        lengthAdjust="spacing"
                                    >
                                        Handmade with love · Cakes'alot by Indu ·
                                    </textPath>
                                </text>
                            </svg>
                            <Cake className="size-6 text-leaf" />
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
